/**
 * THE SKULL — where the head's bone-hard volume sits, measured from the joints
 * and the face, never from the extent of Head-weighted flesh (ADR-039; the
 * probe brief docs/team-briefs/HAIR_CONNECTEDNESS_PROBE_2026-09-30.md, which
 * calibrated every constant below). Promoted from the probe's
 * `app/src/viewport/hair-skull.ts` for T1: the hair detector (hair-region.ts)
 * and the Hair card read it, and the probe keeps measuring with it.
 *
 * The skull is an axis-aligned ellipsoid in the HEAD FRAME: origin at the Head
 * joint, u = the character's left (up × forward), v = up, w = forward, the
 * forward being the BODY's measured frame (detectTail, from the feet) — never
 * the most-forward vertex. Both methods fix the CENTRE first and only then
 * size the ellipsoid as the largest about that fixed centre (a shape grid)
 * that pierces ≤ 0.5% of the head + neck skin. A fixed centre is what keeps a
 * column of hair from pulling the skull up into itself; the size then rests on
 * the face and the nape.
 *
 *  - `eyeSkull`: the eye bones fix the midline and eye height, the face fixes
 *    the scale (F = eye → chin), centre = (eye midline, eye + 0.13 F, Head depth).
 *  - `neckSkull`: NO eye bones (Meshy, native 41-joint Tripo, Mixamo-rigged). Measured from BELOW:
 *      - scale = the neck length NL (lowest neck joint → Head joint), a joint distance hair cannot touch;
 *      - anchor = the CHIN, the lowest Head-owned midline point ahead of the Head joint (a face landmark —
 *        it travels across rig families where the Head joint does not: the 78-joint presets set the chin
 *        0.6–1.0 NL under the Head joint, native 41-joint Tripo 0–0.2 NL over it). When that point sits
 *        under the neck base the Head bone owns the neck (Meshy), and the chin is the lowest Head-owned
 *        point standing proud of the throat instead;
 *      - centre = chin + NECK_GAMMA NL on the Head joint's midline and depth, then slid ±SEARCH NL to the
 *        height whose inscribed skull is largest — the window is what keeps it out of the hair;
 *      - crown capped at chin + CROWN_CAP NL and at an AUTHORED HeadTop_End / head_end (never Harry's
 *        `_synth` tips) — a cap, not an anchor: such end bones sit at the top of the head FLESH, hair included.
 *    Constants are calibrated by hiding the eye bones of 30 eye-bone bodies (the brief's hide-the-eyes
 *    table). It REFUSES, with the reason, when it finds no chin, when head flesh does not enclose the
 *    centre, or when the skull comes out squeezed (R < MIN_R_PER_NL NL: the Head joint is not at the
 *    skull base — the crocodile, Kane's dragon head). It never guesses silently.
 *
 * `placeSkull` is the product door: the eye method, then `neckSkull`, and a
 * LOUD refusal naming both reasons otherwise. A person's adjustment (the Hair
 * card's one-drag confirm) arrives as a `SkullDeclaration` in BIND-WORLD and is
 * read back into the head frame by `skullFromDeclaration`, never re-measured.
 *
 * Depends on: nothing (plain arrays; engine-free). Depended on by:
 * hair/hair-region.ts, the app's Hair tool, hair-connectedness-probe.test.ts.
 */

export interface Ellipsoid {
  readonly c: readonly [number, number, number];
  readonly r: readonly [number, number, number];
}

/** Signed radial distance to the ellipsoid along the ray from its centre (+ outside). */
export function radial(e: Ellipsoid, x: number, y: number, z: number): number {
  const dx = x - e.c[0];
  const dy = y - e.c[1];
  const dz = z - e.c[2];
  const k = Math.hypot(dx / e.r[0], dy / e.r[1], dz / e.r[2]);
  return k === 0 ? -Math.min(...e.r) : Math.hypot(dx, dy, dz) * (1 - 1 / k);
}

export const pct = (xs: ArrayLike<number>, q: number): number => {
  if (xs.length === 0) return NaN;
  const s = Float64Array.from(xs as ArrayLike<number>).sort();
  return s[Math.min(s.length - 1, Math.max(0, Math.round(q * (s.length - 1))))]!;
};

interface Bone {
  readonly name: string;
  readonly parent: string | null;
  readonly worldPosition: { x: number; y: number; z: number };
}

/** The subject in the head frame, with what both skull methods and the probe read. */
export interface HeadBody {
  readonly h: number;
  readonly bones: readonly Bone[];
  readonly headBone: number;
  readonly headSub: ReadonlySet<number>;
  /** head-frame positions u, v, w interleaved */
  readonly L: Float32Array;
  readonly n: number;
  /** dominant bone per vertex */
  readonly dominant: Int32Array;
  /** summed Head-subtree weight per vertex */
  readonly headW: Float32Array;
  /** one representative per welded position */
  readonly reps: readonly number[];
  readonly headOwned: (i: number) => boolean;
  readonly neckOwned: (i: number) => boolean;
  readonly toLocal: (p: { x: number; y: number; z: number }) => [number, number, number];
  /** The inverse of `toLocal`: head frame (u, v, w) → bind-world. */
  readonly toWorld: (u: number, v: number, w: number) => { x: number; y: number; z: number };
  /** The body's measured forward (unit, horizontal) — the head frame's w axis. */
  readonly forward: { readonly x: number; readonly z: number };
  /** HeadTop_End or the like, head frame, when the rig has one */
  readonly crown: { readonly name: string; readonly at: readonly [number, number, number] } | null;
}

export function headBody(
  arrays: { positions: Float32Array; skin: { index: ArrayLike<number>; weight: ArrayLike<number> } | null; bones: readonly Bone[] },
  frame: { height: number; forward: { x: number; z: number } },
  reps: readonly number[],
  /** bones the skin does not list (Mixamo's HeadTop_End, Meshy's head_end, Harry's synthesized tips) — for the crown only */
  extraBones: readonly Bone[] = [],
): HeadBody | string {
  const bones = arrays.bones;
  // three's loader strips ':' from names, so Mixamo's `mixamorig:Head` arrives as `mixamorigHead`
  const headBone = bones.findIndex((b) => /(^|_|:|mixamorig\d*)Head$/i.test(b.name));
  if (headBone < 0) return 'no Head bone';
  if (arrays.skin === null) return 'no skin weights';
  const byName = new Map(bones.map((b, i) => [b.name, i]));
  const headSub = new Set<number>([headBone]);
  for (let grew = true; grew; ) {
    grew = false;
    bones.forEach((b, i) => {
      if (!headSub.has(i) && b.parent !== null && headSub.has(byName.get(b.parent) ?? -1)) {
        headSub.add(i);
        grew = true;
      }
    });
  }
  const H = bones[headBone]!.worldPosition;
  const fw = frame.forward;
  const toLocal = (p: { x: number; y: number; z: number }): [number, number, number] => {
    const dx = p.x - H.x;
    const dz = p.z - H.z;
    return [dx * fw.z - dz * fw.x, p.y - H.y, dx * fw.x + dz * fw.z];
  };
  const P = arrays.positions;
  const n = P.length / 3;
  const L = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) L.set(toLocal({ x: P[i * 3]!, y: P[i * 3 + 1]!, z: P[i * 3 + 2]! }), i * 3);
  const headW = new Float32Array(n);
  const dominant = new Int32Array(n);
  const sk = arrays.skin;
  for (let i = 0; i < n; i++) {
    let best = -1;
    let bw = -1;
    for (let k = 0; k < 4; k++) {
      const b = sk.index[i * 4 + k]!;
      const w = sk.weight[i * 4 + k]!;
      if (headSub.has(b)) headW[i]! += w;
      if (w > bw) {
        bw = w;
        best = b;
      }
    }
    dominant[i] = best;
  }
  // the crown bone: a HeadTop_End / head_end under the Head, skinned or not
  const all = [...bones, ...extraBones];
  const allByName = new Map(all.map((x) => [x.name, x]));
  const underHead = (x: Bone): boolean => {
    for (let p = x.parent; p !== null; p = allByName.get(p)?.parent ?? null) if (p === bones[headBone]!.name) return true;
    return false;
  };
  // Harry's own synthesized tips (`*_synth`) are a guess, not the rig's: on the panda's hard hat one sat 0.18 neck
  // lengths UNDER the eye skull's top, and as a cap it squeezed the skull. Only an authored end bone caps.
  const crownBone = all.find((x) => /head_?top|head_?end/i.test(x.name) && !/synth/i.test(x.name) && underHead(x)) ?? null;
  // u = left = (fw.z, 0, −fw.x), v = up, w = forward = (fw.x, 0, fw.z) — the transpose of toLocal
  const toWorld = (u: number, v: number, w: number): { x: number; y: number; z: number } => ({
    x: H.x + u * fw.z + w * fw.x,
    y: H.y + v,
    z: H.z - u * fw.x + w * fw.z,
  });
  return {
    h: frame.height,
    bones,
    toWorld,
    forward: { x: fw.x, z: fw.z },
    crown: crownBone === null ? null : { name: crownBone.name, at: toLocal(crownBone.worldPosition) },
    headBone,
    headSub,
    L,
    n,
    dominant,
    headW,
    reps,
    headOwned: (i) => headSub.has(dominant[i]!),
    neckOwned: (i) => /neck/i.test(bones[dominant[i]!]!.name),
    toLocal,
  };
}

export interface Skull {
  readonly e: Ellipsoid;
  /** mean radius ∛(rx·ry·rz) */
  readonly R: number;
  /** eye height (measured, or implied by the centre) and chin height, head frame */
  readonly vE: number;
  readonly vChin: number;
  /** eye → chin */
  readonly F: number;
  readonly method: string;
  /** what the inscribed fit rests on */
  readonly rests: string;
}

/** An inscribed ellipsoid may pierce this share of the head + neck surface (eye sockets, mouth bag, stray cards). */
export const PIERCE = 0.005;

/** Largest ellipsoid about a FIXED centre over a shape grid, piercing ≤ PIERCE of the head + neck flesh; `topCap` bounds its crown. */
function inscribe(b: HeadBody, c: [number, number, number], topCap: number): Ellipsoid {
  const { L } = b;
  const constraint = b.reps.filter((i) => b.headOwned(i) || b.neckOwned(i));
  const at = Math.max(0, Math.floor(PIERCE * constraint.length));
  const k = new Float64Array(constraint.length);
  let best: Ellipsoid | null = null;
  for (const bx of [0.6, 0.7, 0.8, 0.9, 1.0, 1.1])
    for (const by of [0.8, 0.9, 1.0, 1.1, 1.2]) {
      constraint.forEach((i, j) => {
        k[j] = Math.hypot((L[i * 3]! - c[0]) / bx, (L[i * 3 + 1]! - c[1]) / by, L[i * 3 + 2]! - c[2]);
      });
      const s = Math.min(Float64Array.from(k).sort()[at]!, topCap / by);
      const e: Ellipsoid = { c, r: [bx * s, by * s, s] };
      if (best === null || e.r[0] * e.r[1] * e.r[2] > best.r[0] * best.r[1] * best.r[2]) best = e;
    }
  return best!;
}

function restsOn(b: HeadBody, e: Ellipsoid, vE: number): string {
  const { L } = b;
  const rests = new Map<string, number>();
  for (const i of b.reps) {
    if (!(b.headOwned(i) || b.neckOwned(i))) continue;
    if (Math.abs(radial(e, L[i * 3]!, L[i * 3 + 1]!, L[i * 3 + 2]!)) >= 0.002) continue;
    const v = L[i * 3 + 1]!;
    const w = L[i * 3 + 2]!;
    const where = b.neckOwned(i) ? 'neck' : v < vE && w > e.c[2] ? 'face' : w > e.c[2] ? 'brow/forehead' : v < e.c[1] ? 'nape' : 'crown/back';
    rests.set(where, (rests.get(where) ?? 0) + 1);
  }
  return [...rests].sort((a, q) => q[1] - a[1]).map(([w, q]) => `${w} ${q}`).join(', ');
}

/** The lowest Head-owned point of the face strip (ahead of the Head joint, within `half` of the midline `u0`). */
function chinOf(b: HeadBody, u0: number, half: number, below: number): number {
  let lo = Infinity;
  for (const i of b.reps)
    if (b.headOwned(i) && Math.abs(b.L[i * 3]! - u0) < half && b.L[i * 3 + 2]! > 0 && b.L[i * 3 + 1]! < below) lo = Math.min(lo, b.L[i * 3 + 1]!);
  return lo;
}

/** The eye method: the eye bones fix midline and eye height, the face fixes the scale. Null without eye bones. */
export function eyeSkull(b: HeadBody): Skull | null {
  const eyeL = b.bones.find((x) => /(^|_)L_?Eye$|Left_?Eye$/i.test(x.name));
  const eyeR = b.bones.find((x) => /(^|_)R_?Eye$|Right_?Eye$/i.test(x.name));
  if (!eyeL || !eyeR) return null;
  const a = b.toLocal(eyeL.worldPosition);
  const r = b.toLocal(eyeR.worldPosition);
  const uE = (a[0] + r[0]) / 2;
  const vE = (a[1] + r[1]) / 2;
  const ipd = Math.abs(a[0] - r[0]);
  const vChin = chinOf(b, uE, Math.max(0.25 * ipd, 0.004 * b.h), vE);
  const F = vE - vChin;
  const c: [number, number, number] = [uE, vE + 0.13 * F, 0];
  const e = inscribe(b, c, Infinity);
  return { e, R: Math.cbrt(e.r[0] * e.r[1] * e.r[2]), vE, vChin, F, method: `eye bones (IPD ${(ipd * 1000).toFixed(0)} mm)`, rests: restsOn(b, e, vE) };
}

/**
 * Calibrated on the eye-bone bodies with their eyes hidden (brief, "no eye bones"), medians of the eye skull:
 * GAMMA — centre over the chin, in neck lengths (humans 1.5–2.1, all 0.8–2.8; median ≈ 1.6), the search's seed.
 * Rejected by the same test and kept only as reported VARIANT lines: THROAT_ALPHA (centre = 1.38 throat radii over
 * the Head joint — p90 error 1.05 R: collars, tusks and beards sit on the throat) and NECK_BETA (centre = 0.9 NL
 * over the Head joint — good on the 78-joint presets, but the Head joint sits ~0.8 NL lower on native Tripo).
 */
export const THROAT_ALPHA = 1.38;
export const NECK_BETA = 0.9;
export const NECK_GAMMA = 1.6;
/** The chin is searched no lower than this many neck lengths under the Head joint. */
const CHIN_FLOOR = 1.5;
/** A chin stands this many neck lengths ahead of the throat's front. */
const NECK_OWNED_MARGIN = 0.15;
/** (NECK_OWNED_MARGIN: a lowest Head-owned point this far under the lowest neck joint means the Head owns the neck.) */
const CHIN_PROUD = 0.15;
export const CROWN_CAP = 3.5;
const SEARCH = 0.3;
/** Refuse a no-eye skull whose mean radius is under this many neck lengths. */
const MIN_R_PER_NL = 0.5;
/** The base-of-neck slab is ±this × the neck-joint length about the lowest neck joint. */
const THROAT_SLAB = 0.25;
/** The throat sector: within this angle of forward. */
const THROAT_DEG = 30;

export interface NeckReading {
  /** throat radius: the innermost surface ahead of the neck axis at the neck base (NaN: not found) */
  readonly rT: number;
  /** Head joint → lowest neck joint */
  readonly neckLength: number;
  readonly lowNeck: string;
  /** the lowest neck joint's height, head frame */
  readonly lowNeckV: number;
  readonly crownBone: string | null;
}

/** What the neck method reads, or why it cannot. Exported so the probe can calibrate on eye bodies. */
export function readNeck(b: HeadBody): NeckReading | string {
  const { bones, L } = b;
  const byName = new Map(bones.map((x, i) => [x.name, i]));
  let low = -1;
  for (let at = byName.get(bones[b.headBone]!.parent ?? '') ?? -1; at >= 0 && /neck/i.test(bones[at]!.name); at = byName.get(bones[at]!.parent ?? '') ?? -1) low = at;
  if (low < 0) low = byName.get(bones[b.headBone]!.parent ?? '') ?? -1;
  if (low < 0) return 'the Head bone has no parent to read a neck from';
  const N = b.toLocal(bones[low]!.worldPosition);
  const neckLength = Math.hypot(...N);
  if (neckLength <= 0) return 'the neck joint sits on the Head joint';
  const cos = Math.cos((THROAT_DEG * Math.PI) / 180);
  let rT = Infinity;
  let count = 0;
  for (const i of b.reps) {
    const v = L[i * 3 + 1]!;
    if (Math.abs(v - N[1]) > THROAT_SLAB * neckLength) continue;
    const du = L[i * 3]! - N[0];
    const dw = L[i * 3 + 2]! - N[2];
    const d = Math.hypot(du, dw);
    if (d === 0 || dw / d < cos) continue;
    count++;
    rT = Math.min(rT, d);
  }
  if (count < 5) rT = NaN; // no throat found (sparse or covered neck base) — only the throat variant needs it
  return { rT, neckLength, lowNeck: bones[low]!.name, lowNeckV: N[1], crownBone: b.crown?.name ?? null };
}

export type NeckVariant = 'throat' | 'neck-length' | 'chin-search';

/** The no-eye method: centre over the Head joint at a calibrated height, crown capped, size inscribed. A string is a refusal. */
export function neckSkull(b: HeadBody, variant: NeckVariant = 'chin-search'): Skull | string {
  const reading = readNeck(b);
  if (typeof reading === 'string') return reading;
  const { neckLength } = reading;
  if (variant === 'throat' && !Number.isFinite(reading.rT)) return 'no throat: too few vertices ahead of the neck axis at the neck base';
  // the CHIN: the lowest Head-owned point of the midline face strip ahead of the Head joint, no lower than
  // CHIN_FLOOR neck lengths under it (a beard or bangs hanging to the chest is not a chin)
  const mid = (i: number): boolean => Math.abs(b.L[i * 3]!) < 0.15 * neckLength && b.L[i * 3 + 2]! > 0;
  let vChin = Infinity;
  for (const i of b.reps) {
    const v = b.L[i * 3 + 1]!;
    if (b.headOwned(i) && mid(i) && v > -CHIN_FLOOR * neckLength) vChin = Math.min(vChin, v);
  }
  if (!Number.isFinite(vChin)) return 'no chin: no Head-owned face strip ahead of the Head joint';
  let chinSay = 'chin = lowest Head-owned midline point';
  // A "chin" under the neck base means the Head bone owns the neck (Meshy: down to the shoulders, 1.23 NL under
  // the Head joint). Then the chin is the lowest Head-owned point standing CHIN_PROUD neck lengths ahead of the
  // throat's front (the midline surface at the neck base) — the jaw over the throat.
  if (vChin < reading.lowNeckV - NECK_OWNED_MARGIN * neckLength) {
    let wThroat = -Infinity;
    for (const i of b.reps) if (mid(i) && Math.abs(b.L[i * 3 + 1]! - reading.lowNeckV) < THROAT_SLAB * neckLength) wThroat = Math.max(wThroat, b.L[i * 3 + 2]!);
    let proud = Infinity;
    for (const i of b.reps)
      if (b.headOwned(i) && mid(i) && b.L[i * 3 + 1]! > reading.lowNeckV && b.L[i * 3 + 2]! > wThroat + CHIN_PROUD * neckLength) proud = Math.min(proud, b.L[i * 3 + 1]!);
    if (!Number.isFinite(proud)) return `no chin: the Head bone owns the neck (${(vChin / neckLength).toFixed(2)} NL) and nothing stands proud of the throat`;
    chinSay = `the Head owns the neck (lowest point ${(vChin / neckLength).toFixed(2)} NL) → chin = lowest point proud of the throat`;
    vChin = proud;
  }
  // throat: ALPHA r_t over the Head joint (rejected, kept for the record); otherwise GAMMA neck lengths over the CHIN —
  // the chin, a face landmark, travels across rig families where the Head joint does not (78-joint presets put
  // the chin 0.5–1.2 NL under the Head joint; native 41-joint Tripo and Harry's Mixamo exports put it 0–0.2 NL over)
  let cy = variant === 'throat' ? THROAT_ALPHA * reading.rT : variant === 'neck-length' ? NECK_BETA * neckLength : vChin + NECK_GAMMA * neckLength;
  const enclosedAt = (y: number): string[] => {
    const dirs: [number, number, number][] = [
      [0, 1, 0],
      [0, 0, 1],
      [0, 0, -1],
      [1, 0, 0],
      [-1, 0, 0],
    ];
    const seen = dirs.map(() => false);
    for (const i of b.reps) {
      if (!b.headOwned(i)) continue;
      const d = [b.L[i * 3]!, b.L[i * 3 + 1]! - y, b.L[i * 3 + 2]!];
      const len = Math.hypot(d[0]!, d[1]!, d[2]!) || 1;
      dirs.forEach((q, k) => {
        if ((d[0]! * q[0] + d[1]! * q[1] + d[2]! * q[2]) / len > Math.SQRT1_2) seen[k] = true;
      });
    }
    return ['above', 'ahead', 'behind', 'left', 'right'].filter((_, k) => !seen[k]);
  };
  const crown = b.crown?.at ?? null;
  // the crown cap: the skull's top never above CROWN_CAP neck lengths over the CHIN, nor above an authored HeadTop_End
  // (a cap, never an anchor: Mixamo-style end bones sit at the top of the head FLESH, hair included)
  const topAt = (y: number): number => Math.min(vChin + CROWN_CAP * neckLength, crown === null ? Infinity : crown[1]) - y;
  if (variant === 'chin-search') {
    // let the centre slide ±SEARCH neck lengths to the height whose inscribed skull is largest
    let best = { y: cy, vol: -1 };
    for (let k = -6; k <= 6; k++) {
      const y = cy + (k / 6) * SEARCH * neckLength;
      if (enclosedAt(y).length > 0) continue;
      const e = inscribe(b, [0, y, 0], topAt(y));
      const vol = e.r[0] * e.r[1] * e.r[2];
      if (vol > best.vol) best = { y, vol };
    }
    cy = best.y;
  }
  const open = enclosedAt(cy);
  if (open.length > 0) return `the centre ${(cy * 1000).toFixed(0)} mm over the Head joint is not enclosed by head flesh (${open.join(', ')})`;
  const c: [number, number, number] = [0, cy, 0];
  const e = inscribe(b, c, topAt(cy));
  const R = Math.cbrt(e.r[0] * e.r[1] * e.r[2]);
  // a skull squeezed far below the neck's scale means the Head joint is not at the skull base (a snout, a
  // high-set head joint: the crocodile 0.17, the walrus 0.30 against ≥ 0.65 on every body the eyes confirm)
  if (R < MIN_R_PER_NL * neckLength)
    return `skull squeezed: R ${(R * 1000).toFixed(0)} mm is ${(R / neckLength).toFixed(2)} neck lengths (< ${MIN_R_PER_NL}) — the Head joint does not sit at the skull base`;
  // the eye height the centre implies (c = vE + 0.13 F, F = vE − chin) — for the face mask only
  const F = (cy - vChin) / 1.13;
  const vE = vChin + F;
  const how =
    variant === 'throat'
      ? `throat r_t ${(reading.rT * 1000).toFixed(0)} mm, centre ${THROAT_ALPHA} r_t over Head`
      : variant === 'neck-length'
        ? `neck length ${(neckLength * 1000).toFixed(0)} mm (${reading.lowNeck}→Head), centre ${NECK_BETA} NL over Head`
        : `neck length ${(neckLength * 1000).toFixed(0)} mm (${reading.lowNeck}→Head), centre ${((cy - vChin) / neckLength).toFixed(2)} NL over the chin (searched from ${NECK_GAMMA}; ${chinSay})`;
  return {
    e,
    R,
    vE,
    vChin,
    F,
    method: `no-eye ${variant}: ${how}, crown ≤ chin + ${CROWN_CAP} NL${crown !== null ? ` and ≤ ${reading.crownBone}` : ''}`,
    rests: restsOn(b, e, vE),
  };
}

// ─── the product door (ADR-039 T1) ─────────────────────────────────────────

/**
 * The skull as a DECLARATION, in bind-world: its centre, its semi-axes along the
 * body's (left, up, forward), and the body's facing those axes were read in
 * (degrees about +Y, 0 = facing +Z). The Document's `target.hair.skull` has
 * this shape (zod-gated there); a person's drag lands here.
 */
export interface SkullDeclarationShape {
  readonly centre: { readonly x: number; readonly y: number; readonly z: number };
  readonly semiAxes: { readonly x: number; readonly y: number; readonly z: number };
  readonly yawDeg: number;
}

/** A declared skull's facing may differ from the re-measured body's by this much before it refuses. */
export const SKULL_YAW_TOLERANCE_DEG = 2;

/** The body's facing in degrees about +Y (0 = facing +Z) — the yaw a declaration records. */
export function skullYawDeg(b: HeadBody): number {
  return (Math.atan2(b.forward.x, b.forward.z) * 180) / Math.PI;
}

/**
 * The eye method, then the neck method; a refusal names why neither placed a
 * skull. Never a guess: a body the probe could not place (the crocodile, Kane's
 * dragon head, a rig with no Head bone) is said so, and nothing downstream runs.
 */
export function placeSkull(b: HeadBody): Skull | string {
  const eyes = eyeSkull(b);
  // An eye body whose face strip has no chin under the eyes reads F ≤ 0: not a skull, so the neck decides.
  if (eyes !== null && Number.isFinite(eyes.F) && eyes.F > 0 && eyes.R > 0) return eyes;
  const neck = neckSkull(b);
  if (typeof neck === 'string') return `${eyes === null ? 'no eye bones' : 'the eye bones found no chin under them'}, and the neck method refused — ${neck}`;
  return neck;
}

/** A skull (head frame) as the bind-world declaration the Document stores. */
export function skullToDeclaration(b: HeadBody, e: Ellipsoid): SkullDeclarationShape {
  const c = b.toWorld(e.c[0], e.c[1], e.c[2]);
  return { centre: c, semiAxes: { x: e.r[0], y: e.r[1], z: e.r[2] }, yawDeg: skullYawDeg(b) };
}

/**
 * A declared skull read back into THIS body's head frame. It refuses when the
 * body's facing moved past the tolerance since the declaration was made: the
 * semi-axes were the body's left / up / forward then, and silently re-aiming
 * them would put a person's skull somewhere they never put it.
 */
export function skullFromDeclaration(b: HeadBody, d: SkullDeclarationShape): Ellipsoid | string {
  const drift = Math.abs(((skullYawDeg(b) - d.yawDeg + 540) % 360) - 180);
  if (drift > SKULL_YAW_TOLERANCE_DEG) {
    return `the declared skull was read with the body facing ${d.yawDeg.toFixed(1)}°; this body faces ${skullYawDeg(b).toFixed(1)}° — re-confirm the skull`;
  }
  const r = [d.semiAxes.x, d.semiAxes.y, d.semiAxes.z] as const;
  if (!r.every((x) => Number.isFinite(x) && x > 0)) return 'the declared skull has a non-positive semi-axis';
  return { c: b.toLocal(d.centre), r };
}

/**
 * A skull a person placed (or a Document declared): the ellipsoid is theirs;
 * the face readings (eye and chin heights, F) stay the measurement's, because
 * they come from the face, which the drag did not move. With no measurement to
 * lean on they are implied by the ellipsoid itself, from the eye skulls'
 * shape (top ≈ 1 F over the eyes, centre 0.13 F over them → F ≈ ry / 0.87).
 */
export function adoptSkull(measured: Skull | null, e: Ellipsoid, method: string): Skull {
  const R = Math.cbrt(e.r[0] * e.r[1] * e.r[2]);
  if (measured !== null) return { e, R, vE: measured.vE, vChin: measured.vChin, F: measured.F, method, rests: 'placed by a person' };
  const F = e.r[1] / 0.87;
  const vE = e.c[1] - 0.13 * F;
  return { e, R, vE, vChin: vE - F, F, method, rests: 'placed by a person' };
}
